import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './IndustrialSoftwarePage.css';

import smilaiPreview from '../assets/smilai_preview.webp';
import factoraiPreview from '../assets/factorai_preview.webp';
import securaiPreview from '../assets/securai_preview.webp';

// Capability card images
import ioTDashboardImg from '../assets/Industrial Automation_service/Industrial_IoT_Dashboards.webp';
import plcImg from '../assets/Industrial Automation_service/PLC_Control_Systems.webp';
import smartFactoryImg from '../assets/Industrial Automation_service/Smart_Factory_Solutions.webp';
import machineMonitoringImg from '../assets/Industrial Automation_service/Machine_Monitoring.webp';
import realTimeTrackingImg from '../assets/Industrial Automation_service/Real-Time_Production_Tracking.webp';
import safetyComplianceImg from '../assets/Industrial Automation_service/Safety_Compliance.webp';
import dataAnalyticsImg from '../assets/Industrial Automation_service/Industrial_Data_Analytics.webp';

// Key Features section — left column image
import featuresImg from '../assets/Industrial Automation_service/Smart_Factory_Solutions.webp';

import realtimeIcon from '../assets/Industrial Automation_icons/Real-Time.webp';
import automatedIcon from '../assets/Industrial Automation_icons/Automated.webp';
import dataDrivenIcon from '../assets/Industrial Automation_icons/Data-Driven.webp';
import protectedIcon from '../assets/Industrial Automation_icons/Protected.webp';
import smarterIcon from '../assets/Industrial Automation_icons/Smarter.webp';
import industryIcon from '../assets/Industrial Automation_icons/Industry.webp';

import {
  ArrowRight, ChevronDown, CheckCircle,
  ShieldAlert,
  Factory, HeartHandshake
} from 'lucide-react';

interface IndustrialSoftwarePageProps {
  onBookConsultation?: () => void;
}

interface UseCaseCardProps {
  useCase: {
    title: string;
    desc: string;
    link?: string;
    linkLabel?: string;
  };
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}

const UseCaseCard: React.FC<UseCaseCardProps> = ({ useCase, index, isExpanded, onToggle }) => {
  const formattedIndex = String(index + 1).padStart(2, '0');

  return (
    <div
      className={`industrial-solution-list-item ${isExpanded ? 'expanded' : ''}`}
      onClick={onToggle}
    >
      <div className="industrial-solution-list-header">
        <div className="industrial-solution-list-left">
          <span className="industrial-solution-list-index">{formattedIndex}</span>
          <h3 className="industrial-solution-list-title">{useCase.title}</h3>
        </div>
      </div>

      <div className="industrial-solution-list-body">
        <p className="industrial-solution-list-preview">{useCase.desc}</p>
      </div>
    </div>
  );
};

/* ─── Capability Cards Data ─── */
const capabilities = [
  {
    image: ioTDashboardImg,
    title: 'Industrial IoT Dashboards',
    desc: 'Monitor machines and production with real-time insights.',
  },
  {
    image: plcImg,
    title: 'PLC Control Systems',
    desc: 'Automate machines with reliable and precise PLC solutions.',
  },
  {
    image: smartFactoryImg,
    title: 'Smart Factory Solutions',
    desc: 'Connect people, machines, and processes for smarter manufacturing.',
  },
  {
    image: machineMonitoringImg,
    title: 'Machine Monitoring',
    desc: 'Track equipment health to reduce downtime and improve performance.',
  },
  {
    image: realTimeTrackingImg,
    title: 'Real-Time Production Tracking',
    desc: 'Monitor production live and optimize operational efficiency.',
  },
  {
    image: safetyComplianceImg,
    title: 'Safety & Compliance',
    desc: 'Enhance workplace safety while meeting industry standards.',
  },
  {
    image: dataAnalyticsImg,
    title: 'Industrial Data Analytics',
    desc: 'Turn operational data into actionable business insights.',
  },
];

/* ─── Business Challenges Data ─── */
const businessChallenges = [
  {
    title: 'Unplanned Equipment Downtime',
    desc: 'Detect machine failures early with predictive monitoring that keeps production lines running and reduces costly downtime.',
    link: '/solutions',
    linkLabel: 'View Monitoring Solutions'
  },
  {
    title: 'Lack of Production Visibility',
    desc: 'Replace manual reporting with real-time dashboards that show live production status, OEE, and performance metrics.',
    link: '/products',
    linkLabel: 'View IIoT Solutions'
  },
  {
    title: 'Manual & Paper-Based Processes',
    desc: 'Digitize shop floor operations, work orders, and quality checklists to improve accuracy and eliminate manual work.',
    link: '/solutions',
    linkLabel: 'View Automation Solutions'
  },
  {
    title: 'Safety & Compliance Risks',
    desc: 'Manage safety incidents, permits, and compliance audits digitally to maintain a safe and audit-ready facility.',
    link: '/case-studies',
    linkLabel: 'View Case Studies'
  }
];

/* ─── Key Features Data ─── */
const keyFeatures = [
  { icon: <img loading="lazy" src={realtimeIcon} alt="Real-Time" style={{ width: '100%', height: '100%', objectFit: 'contain' }}  title="Vivify Soft Tech" />, metric: 'Real-Time', title: 'Machine Monitoring', desc: 'Track equipment health and performance live on the shop floor.' },
  { icon: <img loading="lazy" src={automatedIcon} alt="Automated" style={{ width: '100%', height: '100%', objectFit: 'contain' }}  title="Vivify Soft Tech" />, metric: 'Automated', title: 'PLC & SCADA Control', desc: 'Automate production with reliable PLC and SCADA integration.' },
  { icon: <img loading="lazy" src={dataDrivenIcon} alt="Data-Driven" style={{ width: '100%', height: '100%', objectFit: 'contain' }}  title="Vivify Soft Tech" />, metric: 'Data-Driven', title: 'Production Analytics', desc: 'Turn operational data into actionable insights and decisions.' },
  { icon: <img loading="lazy" src={protectedIcon} alt="Protected" style={{ width: '100%', height: '100%', objectFit: 'contain' }}  title="Vivify Soft Tech" />, metric: 'Protected', title: 'Safety & Compliance', desc: 'Manage safety incidents and stay audit-ready at all times.' },
  { icon: <img loading="lazy" src={smarterIcon} alt="Smarter" style={{ width: '100%', height: '100%', objectFit: 'contain' }}  title="Vivify Soft Tech" />, metric: 'Smarter', title: 'Predictive Maintenance', desc: 'Predict failures before they happen and eliminate unplanned downtime.' },
  { icon: <img loading="lazy" src={industryIcon} alt="Industry 4.0" style={{ width: '100%', height: '100%', objectFit: 'contain' }}  title="Vivify Soft Tech" />, metric: 'Industry 4.0', title: 'Smart Factory Ready', desc: 'Build connected, scalable, and future-ready manufacturing operations.' },
];

/* ─── Related Products Data ─── */
const relatedProducts = [
  {
    icon: <ShieldAlert size={24} />,
    name: 'SecurAI',
    desc: 'Industrial safety compliance and incident management platform.',
    link: '/products/securai',
    image: securaiPreview,
  },
  {
    icon: <Factory size={24} />,
    name: 'FactorAI',
    desc: 'Intelligent manufacturing platform for production monitoring and industrial automation.',
    link: '/products/factorai',
    image: factoraiPreview,
  },
  {
    icon: <HeartHandshake size={24} />,
    name: 'SmilAI',
    desc: 'Practice management with equipment monitoring capabilities.',
    link: '/products/smilai',
    image: smilaiPreview,
  },
];

/* ─── FAQs ─── */
const faqs = [
  {
    q: 'What industrial automation services does Vivify Soft Tech offer?',
    a: 'Vivify Soft Tech provides PLC, SCADA, HMI, IIoT, machine monitoring, production tracking, and custom industrial automation solutions.'
  },
  {
    q: 'How can industrial automation improve manufacturing?',
    a: 'Industrial automation increases productivity, reduces downtime, improves quality, and provides real-time operational visibility.'
  },
  {
    q: 'Can Vivify Soft Tech\'s automation solutions integrate with existing systems?',
    a: 'Yes. Vivify Soft Tech\'s solutions integrate with PLCs, SCADA, ERP, MES, IoT devices, and third-party industrial systems.'
  },
  {
    q: 'Which industries does Vivify Soft Tech serve?',
    a: 'Vivify Soft Tech delivers industrial automation solutions for manufacturing, automotive, pharmaceuticals, food processing, logistics, and other industrial sectors.'
  },
  {
    q: 'Why choose Vivify Soft Tech for industrial automation?',
    a: 'Vivify Soft Tech builds reliable, scalable, and Industry 4.0-ready automation solutions that improve efficiency, productivity, and long-term business growth.'
  },
];

export const IndustrialSoftwarePage: React.FC<IndustrialSoftwarePageProps> = ({ onBookConsultation: _onBookConsultation }) => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [openChallenge, setOpenChallenge] = useState<number | null>(null);
  const [visibleCards, setVisibleCards] = useState<boolean[]>(new Array(keyFeatures.length).fill(false));
  const featuresRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
            keyFeatures.forEach((_, index) => {
              setTimeout(() => {
                setVisibleCards(prev => {
                  const newVisible = [...prev];
                  newVisible[index] = true;
                  return newVisible;
                });
              }, index * 150);
            });
          }
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -100px 0px' }
    );

    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      setVisibleCards(new Array(keyFeatures.length).fill(true));
    } else {
      if (featuresRef.current) observer.observe(featuresRef.current);
    }

    return () => {
      if (featuresRef.current) observer.unobserve(featuresRef.current);
    };
  }, []);

  return (
    <main className="industrial-page" style={{ paddingTop: '80px', overflowX: 'hidden' }}>

      {/* ── SEO Meta ── */}
      {(() => { document.title = 'Industrial Software & Automation | Vivify Soft Tech'; return null; })()}

      {/* ════════════ HERO ════════════ */}
      <section
        className="industrial-hero"
        aria-label="Industrial Software & Automation Hero"
        style={{ position: 'relative' }}
      >
        <img 
          src="/Industrial_Hero_section.webp" 
          alt="" 
          fetchPriority="high" 
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: 'center right',
            zIndex: 0
          }}
        />
        <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(75deg, rgba(5, 9, 21, 0.85) 0%, rgba(5, 9, 21, 0.85) 60%, rgba(5, 9, 21, 0) 60.1%, rgba(5, 9, 21, 0) 100%)',
            zIndex: 1
        }} />
        <div className="industrial-hero-bg" style={{ zIndex: 2 }}>
          <div className="industrial-hero-grid" />
          <div className="industrial-hero-orb-1" style={{ opacity: 0.5 }} />
          <div className="industrial-hero-orb-2" style={{ opacity: 0.5 }} />
        </div>

        <div className="container industrial-hero-inner" style={{ position: 'relative', zIndex: 3 }}>
          <div className="industrial-hero-content">
            <h1 className="industrial-hero-h1">
              Industrial Automation Services for{' '}
              <span className="accent">Smarter Manufacturing</span>
            </h1>

            <p className="industrial-hero-meta">
              Modernize your production environment with scalable PLC, SCADA, HMI, IIoT, and industrial automation solutions that improve performance, reduce operational costs, and support sustainable growth.
            </p>

            <div className="industrial-hero-actions">
              <Link to="/services" className="industrial-hero-primary-btn">
                Explore Services <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ CAPABILITIES ════════════ */}
      <section className="industrial-capabilities-section" aria-label="Industrial Capabilities">
        <div className="container">
          <div className="industrial-capabilities-grid-layout">
            <div className="industrial-section-header centered">
              <div className="industrial-section-eyebrow" style={{ margin: '0 auto 1rem auto' }}>Capabilities</div>
              <h2 className="industrial-section-title">
                Industrial Solutions That <span className="text-gradient-purple">Transform Operations</span>
              </h2>
              <p className="industrial-section-desc">
                From IoT dashboards and machine monitoring to smart factory systems and automation — every solution is built for Industry 4.0 readiness.
              </p>
            </div>

            <div className="industrial-capabilities-grid">
              {capabilities.map((cap, i) => (
                <div key={i} className="industrial-capability-card">
                  <div className="industrial-capability-card-img-wrap">
                    <img loading="lazy" src={cap.image} alt={cap.title} className="industrial-capability-card-img"  title="Vivify Soft Tech" />
                    <div className="industrial-capability-card-img-overlay" />
                    <h3 className="industrial-capability-card-title-overlay">{cap.title}</h3>
                  </div>
                  <div className="industrial-capability-card-content">
                    <p className="industrial-capability-card-desc">{cap.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ BUSINESS CHALLENGES ════════════ */}
      <section className="industrial-problems-section" aria-label="Business Challenges">
        <div className="container">
          <div className="industrial-problems-split-layout">
            <div className="industrial-problems-left-col">
              <div className="industrial-section-header" style={{ textAlign: 'left', maxWidth: '100%', margin: 0 }}>
                <div className="industrial-problems-custom-tag">
                  <span className="industrial-problems-dot"></span> BUSINESS CHALLENGES
                </div>
                <h2 className="industrial-problems-custom-title">
                  Common Industrial Challenges <span className="text-gradient-purple">We Solve</span>
                </h2>
                <p className="industrial-problems-custom-desc">
                  Transform your manufacturing operations by addressing these key challenges with our comprehensive industrial automation solutions.
                </p>
              </div>
            </div>

            <div className="industrial-problems-right-col">
              <div className="industrial-solution-list-container">
                {businessChallenges.map((challenge, i) => (
                  <UseCaseCard 
                    key={i} 
                    useCase={challenge} 
                    index={i} 
                    isExpanded={openChallenge === i}
                    onToggle={() => setOpenChallenge(openChallenge === i ? null : i)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ KEY FEATURES ════════════ */}
      <section className="industrial-features-section" aria-label="Key Features" ref={featuresRef}>
        <div className="container">
          <div className="industrial-features-layout">
            {/* LEFT COLUMN — header + image */}
            <div className="industrial-features-left">
              <div className="industrial-features-header">
                <div className="industrial-section-eyebrow">Features</div>
                <h2 className="industrial-section-title">
                  Key Features of <span className="text-gradient-purple">Industrial Systems</span>
                </h2>
                <p className="industrial-section-desc">
                  IoT integration, real-time monitoring, performance analytics, safety systems, Industry 4.0 readiness, and predictive maintenance capabilities.
                </p>
              </div>

              <div className="industrial-features-image-wrap">
                <img loading="lazy" src={featuresImg}
                  alt="Industrial engineer reviewing smart factory systems"
                  className="industrial-features-image"
                 title="Vivify Soft Tech" />
              </div>
            </div>

            {/* RIGHT COLUMN — feature cards */}
            <div className="industrial-features-right">
              <div className="industrial-features-grid">
                {keyFeatures.map((feature, i) => (
                  <div
                    key={i}
                    className={`industrial-feature-card ${visibleCards[i] ? 'animate-in' : 'animate-out'}`}
                  >
                    <div className="industrial-feature-icon">
                      {feature.icon}
                    </div>
                    <div className="industrial-feature-text">
                      <div className="industrial-feature-metric">{feature.metric}</div>
                      <div className="industrial-feature-title">{feature.title}</div>
                      <p className="industrial-feature-desc">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ RELATED PRODUCTS ════════════ */}
      <section className="industrial-products-section" aria-label="Related Industrial Products">
        <div className="container">
          <div className="industrial-section-header">
            <div className="industrial-section-eyebrow">Products</div>
            <h2 className="industrial-section-title">
              Vivify Industrial <span className="text-gradient-purple">Product Ecosystem</span>
            </h2>
            <p className="industrial-section-desc">
              Our industrial products serve manufacturing, safety compliance, operations management, and equipment monitoring industries.
            </p>
          </div>

          <div className="industrial-products-grid">
            {relatedProducts.map((p, i) => (
              <Link key={i} to={p.link} className="industrial-product-card">
                <img loading="lazy" src={p.image} alt={`${p.name} Preview`} className="industrial-product-bg-img"  title="Vivify Soft Tech" />
                <div className="industrial-product-dark-overlay" />
                <div className="industrial-product-content-overlay">
                  <h3 className="industrial-product-name">{p.name}</h3>
                  <p className="industrial-product-desc">{p.desc}</p>
                  <span className="industrial-product-link-badge">
                    View Product <ArrowRight size={12} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════ FAQs ════════════ */}
      <section className="industrial-faq-section" aria-label="Frequently Asked Questions">
        <div className="container">
          <div className="industrial-section-header">
            <div className="industrial-section-eyebrow">FAQs</div>
            <h2 className="industrial-section-title">
              Frequently <span className="text-gradient-purple">Asked Questions</span>
            </h2>
            <p className="industrial-section-desc">
              Common questions about industrial software, IoT dashboards, manufacturing automation, and smart factory systems.
            </p>
          </div>

          <div className="industrial-faq-container">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`industrial-faq-item${openFaq === i ? ' open' : ''}`}
              >
                <button
                  className="industrial-faq-trigger"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span>{faq.q}</span>
                  <ChevronDown size={18} className="industrial-faq-chevron" />
                </button>
                <div className="industrial-faq-content">
                  <div className="industrial-faq-body">{faq.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════ FINAL CTA ════════════ */}
      <section className="industrial-cta-section" aria-label="Call to Action">
        <div className="industrial-cta-glow" />
        <div className="container industrial-cta-inner">
          <div className="industrial-section-eyebrow" style={{ justifyContent: 'center', marginBottom: '1.5rem' }}>
            <CheckCircle size={14} /> Transform Your Manufacturing
          </div>
          <h2 className="industrial-cta-title">
            Transform Your Manufacturing with Smart Automation
          </h2>
          <p className="industrial-cta-desc">
            Boost productivity, reduce downtime, and modernize your operations with tailored industrial automation solutions.
          </p>
          <div className="industrial-cta-actions">
            <Link to="/contact" className="industrial-hero-primary-btn">
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="industrial-hero-primary-btn">
              Talk to Our Experts
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
};

export default IndustrialSoftwarePage;